import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import { translateTeamToAbbr } from '../../utils/teamAbbreviations';



const Entries = () => {
  const { user } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [spreadsheetId, setSpreadsheetId] = useState(process.env.REACT_APP_DEFAULT_SPREADSHEET_ID || '');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetchWeeks();
  }, []);

  useEffect(() => {
    if (selectedWeek) {
      fetchEntries();
    }
  }, [selectedWeek]);

  const formatDeadlineDisplay = (deadlineStr) => {
    if (!deadlineStr) return 'Invalid';
    try {
      let dateString = deadlineStr;
      // If no timezone info, treat as HST (-10:00)
      if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
        dateString = dateString + '-10:00';
      }
      const dt = new Date(dateString);
      if (isNaN(dt.getTime())) return 'Invalid Date';
      const options = {
        timeZone: 'Pacific/Honolulu',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      return dt.toLocaleString('en-US', options);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const fetchWeeks = async () => {
    try {
      const response = await api.get('/weeks');
      setWeeks(response.data);
      if (response.data.length > 0) {
        setSelectedWeek(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching weeks:', error);
    }
  };

  const fetchEntries = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/entries/week/${selectedWeek}?user_id=${user?.id}`);
      setEntries(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        alert('Deadline has not passed yet. Entries are not visible.');
        setEntries([]);
      } else {
        console.error('Error fetching entries:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedWeek) return;

    setIsExporting(true);
    try {
      const response = await api.get(`/entries/export/${selectedWeek}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const week = weeks.find((w) => w.id === selectedWeek);
      link.setAttribute('download', `week_${week?.week_number}_entries.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export CSV');
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePushToSheets = () => {
    console.log('Push button clicked!', { selectedWeek, spreadsheetId, entriesCount: entries.length });
    
    if (!selectedWeek || !spreadsheetId) {
      alert('Please select a week and enter a spreadsheet ID');
      return;
    }

    // Show custom confirmation modal instead of window.confirm
    setShowConfirmModal(true);
  };

  const confirmPush = async () => {
    setShowConfirmModal(false);
    console.log('Starting push to Google Sheets...');
    setIsPushing(true);
    
    try {
      console.log('Making API request...');
      const response = await api.post(
        `/sheets/push-entries?user_id=${user?.id}&week_id=${selectedWeek}&spreadsheet_id=${spreadsheetId}`
      );
      
      console.log('Push successful!', response.data);
      
      // Store result and show modal
      setPushResult(response.data);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Push failed:', error);
      
      if (error.response?.status === 401) {
        console.log('Authorization required');
        // Need authorization - redirect directly without confirmation
        alert(
          '🔐 Authorization Required\n\n' +
          'You need to authorize Google Sheets access first.\n\n' +
          'You will be redirected to Google authorization page.\n\n' +
          'After authorizing, come back and try again.'
        );
        
        // Redirect to OAuth login page using full backend URL
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        window.location.href = `${backendUrl}/api/oauth/sheets/login?user_id=${user?.id}`;
      } else {
        alert(
          `❌ Failed to Push to Google Sheets\n\n` +
          `Error: ${error.response?.data?.detail || error.message || 'Unknown error'}\n\n` +
          `Please check:\n` +
          `1. You have authorized Google Sheets\n` +
          `2. The spreadsheet ID is correct\n` +
          `3. You have edit access to the spreadsheet`
        );
      }
    } finally {
      setIsPushing(false);
      console.log('Push operation completed');
    }
  };

  const handleSyncNames = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    
    try {
      const response = await api.post(`/admin/sync-entry-names?user_id=${user?.id}`);
      setSyncResult(response.data);
      
      // Refresh entries to show updated names
      if (selectedWeek) {
        fetchEntries();
      }
      
      alert(`✅ ${response.data.message}`);
    } catch (error) {
      console.error('Sync failed:', error);
      alert(`❌ Failed to sync names: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const selectedWeekInfo = weeks.find((w) => w.id === selectedWeek);

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <h1 className="text-3xl font-bold text-primary mb-6">All Entries</h1>

        <div className="bg-dark-secondary rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Select Week</label>
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {weeks.map((week) => (
                  <option key={week.id} value={week.id}>
                    Week {week.week_number} - {week.season_year}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Actions</label>
              <button
                onClick={handleExportCSV}
                disabled={isExporting || !selectedWeek || entries.length === 0}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {isExporting ? 'Exporting...' : '📥 Export CSV'}
              </button>
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Spreadsheet ID</label>
              <input
                type="text"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                placeholder="Google Sheets ID"
                className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <button
              onClick={handlePushToSheets}
              disabled={isPushing || !selectedWeek || !spreadsheetId || entries.length === 0}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isPushing ? '⏳ Pushing to Google Sheets...' : '📊 Push to Google Sheets'}
            </button>
            
            <button
              onClick={() => {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
                window.open(`${backendUrl}/api/oauth/sheets/login?user_id=${user?.id}`, '_blank');
              }}
              className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition text-sm"
            >
              🔐 Authorize Google Sheets Access
            </button>

            <button
              onClick={handleSyncNames}
              disabled={isSyncing}
              className="w-full bg-purple-600 text-white py-2 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"
            >
              {isSyncing ? '⏳ Syncing Names...' : '🔄 Sync Entry Names from User Profiles'}
            </button>
          </div>

          {selectedWeekInfo && (
            <div className="mt-4 text-gray-300 text-sm">
              Deadline: {formatDeadlineDisplay(selectedWeekInfo.deadline)} HST • 
              {entries.length} entries submitted
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white text-xl">Loading entries...</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="bg-dark-secondary rounded-lg p-6 text-center">
            <p className="text-gray-300 text-xl">
              No entries found for this week
            </p>
          </div>
        ) : (
          <div className="bg-dark-secondary rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-dark">
                  <tr>
                    <th className="px-6 py-4 text-left text-gray-300 font-semibold">Entry Name</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-semibold">Name</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-semibold">Pick 1</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-semibold">Pick 2</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-semibold">Pick 3</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-semibold">Pick 4</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-semibold">Pick 5</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-semibold">Pick 6</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-semibold">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-dark transition">
                      <td className="px-6 py-4 text-white font-semibold">{entry.entry_name}</td>
                      <td className="px-6 py-4 text-gray-300">{entry.name}</td>
                      <td className="px-6 py-4 text-white">{entry.picks[0] ? translateTeamToAbbr(entry.picks[0]) : '-'}</td>
                      <td className="px-6 py-4 text-white">{entry.picks[1] ? translateTeamToAbbr(entry.picks[1]) : '-'}</td>
                      <td className="px-6 py-4 text-white">{entry.picks[2] ? translateTeamToAbbr(entry.picks[2]) : '-'}</td>
                      <td className="px-6 py-4 text-white">{entry.picks[3] ? translateTeamToAbbr(entry.picks[3]) : '-'}</td>
                      <td className="px-6 py-4 text-white">{entry.picks[4] ? translateTeamToAbbr(entry.picks[4]) : '-'}</td>
                      <td className="px-6 py-4 text-white">{entry.picks[5] ? translateTeamToAbbr(entry.picks[5]) : '-'}</td>
                      <td className="px-6 py-4 text-gray-400 text-sm">
                        {formatDeadlineDisplay(entry.submitted_at)} HST
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-dark-secondary rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-white mb-4">Confirm Push to Google Sheets</h2>
            <p className="text-gray-300 mb-2">
              Push <span className="font-bold text-primary">{entries.length} entries</span> to Google Sheets?
            </p>
            <div className="bg-dark rounded-lg p-4 mt-4 text-left">
              <p className="text-gray-300 text-sm mb-1">
                <span className="font-semibold">Spreadsheet ID:</span>
              </p>
              <p className="text-gray-400 text-xs break-all mb-2">{spreadsheetId}</p>
              <p className="text-gray-300 text-sm">
                <span className="font-semibold">Week:</span> {selectedWeekInfo?.week_number} - {selectedWeekInfo?.season_year}
              </p>
            </div>
            <div className="mt-6 space-y-3">
              <button
                onClick={confirmPush}
                className="w-full bg-primary text-white py-3 rounded-lg font-bold hover:bg-green-600 transition"
              >
                ✅ Yes, Push to Sheets
              </button>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="w-full bg-dark text-gray-300 py-3 rounded-lg font-bold hover:bg-gray-700 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && pushResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-dark-secondary rounded-lg p-8 max-w-md w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">✅ Success!</h2>
              <p className="text-gray-300 mb-2">
                Entries pushed to Google Sheets successfully!
              </p>
              <div className="bg-dark rounded-lg p-4 mt-4 text-left">
                <p className="text-gray-300 text-sm mb-1">
                  📊 <span className="font-semibold">Entries:</span> {pushResult.entries_count}
                </p>
                <p className="text-gray-300 text-sm mb-1">
                  📑 <span className="font-semibold">Sheet Tab:</span> {pushResult.sheet_tab}
                </p>
              </div>
            </div>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => {
                  if (pushResult.spreadsheet_url) {
                    window.open(pushResult.spreadsheet_url, '_blank');
                  }
                }}
                className="w-full bg-primary text-white py-3 rounded-lg font-bold hover:bg-green-600 transition"
              >
                📄 Open Spreadsheet
              </button>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setPushResult(null);
                }}
                className="w-full bg-dark text-gray-300 py-3 rounded-lg font-bold hover:bg-gray-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Entries;
